import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import { BASE_URL } from "../../../constant";
import useResendTimer from '../../../hooks/useResendTimer';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
      padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
      padding: theme.spacing(1),
    },
}));

const OtpVerify = ({ openModal, handleClose, phoneNumber }) => {
    const [ otp, setOtp ] = useState({ digit1: '', digit2: '', digit3: '', digit4: '' });
    const [ email, setEmail ] = useState('');
    const [ password, setPassword ] = useState('');
    const [ isDemat, setDemat ] = useState(false);
    const [ userName, setUserName ] = useState('');
    const [ errorMsg, setErrorMsg ] = useState('');
    const [ successMsg, setSuccessMsg ] = useState('');
    const navigate = useNavigate();
    const { disableResend, timer, startTimer } = useResendTimer(45);

    const handleOtpChange = (e, nextInputId) => {
        const { name, value } = e.target;
        if (value.match(/^[0-9]$/)) {
          setOtp({ ...otp, [name]: value });
    
          const nextInput = document.getElementById(nextInputId);
          if (nextInput && value) {
            nextInput.focus();
          } else {
            document.getElementById("email").focus();
          }
        } else if (value === '') {
          setOtp({ ...otp, [name]: '' });
        }
    };
    
    const registerUser = async (otpFlag) => {
        try {
            const { data: user } = await axios.post(BASE_URL+'/isUserExist', { userEmail: email, primaryPhone: phoneNumber });
            const { data } = await axios.post(BASE_URL+'/registerUser', {
                 userName, 
                 userEmail: email, 
                 password, 
                 primaryPhone: phoneNumber, 
                 isDemat, 
                 isOTPVerified: otpFlag,
                 userExist: {
                    isEmailExist: user?.isEmailExist,
                    isMobileExist: user?.isMobileExist,
                 } 
            });
            if(data?.auth) {
                const { auth, msg } = data;
                navigate('/sign-in', {
                    state: {
                        auth, msg
                    }
                });
            }
        } catch(err) {
            setErrorMsg("We encountered an issue, please try after some time or reach out to help@capitaltredns.in");
        }
    }

    const handleOtpSubmit = async () => {
        setErrorMsg('');
        const completeOtp = otp.digit1 + otp.digit2 + otp.digit3 + otp.digit4;
        if (completeOtp.length === 4 && email && password && userName) {
            try {
                const { data } = await axios.post(BASE_URL+'/verifyOtp', { primaryPhone: '+91 '+phoneNumber, otp: completeOtp });
                if(data?.message && data?.status === 1) {
                    registerUser(true);
                } else {
                    setErrorMsg('OTP does not match');
                }
            } catch(err) {
                await registerUser(false);
            }
        } else {
            if(completeOtp.length !== 4){
                setErrorMsg('Invalid OTP');
                return false;
            }
            if(!email) {
                setErrorMsg('Please enter a valid email');
                return false;
            }
            if(!password) {
                setErrorMsg('Please enter a valid password');
                return false;
            }
            if(!userName) {
                setErrorMsg('Please enter a valid name');
                return false;
            }
        }
    };

    const resendOtp = async () => {
        setErrorMsg('');
        try {
            const { data } = await axios.post(BASE_URL+'/resendOtp', { primaryPhone: '+91 '+phoneNumber });
            if(data?.message) {
                setSuccessMsg(data?.message);
                startTimer();
            }
        } catch(err) {
            setErrorMsg('Unable to resend OTP, please try again later');
        }
    }

    return (
        <BootstrapDialog
            onClose={handleClose}
            aria-labelledby="customized-dialog-title"
            open={openModal}
        >
            <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
                Verify
            </DialogTitle>

            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>

            <DialogContent dividers>
                {
                    errorMsg && (
                        <Stack sx={{ width: '100%', marginTop: '10px', marginBottom: '10px' }} spacing={2}>
                            <Alert severity="error" onClose={() => setErrorMsg('')}>{errorMsg}</Alert>
                        </Stack>
                    )
                }
                {
                    successMsg && (
                        <Stack sx={{ width: '100%', marginTop: '10px', marginBottom: '10px' }} spacing={2}>
                            <Alert severity="success" onClose={() => setSuccessMsg('')}>{successMsg}</Alert>
                        </Stack>
                    )
                }
                <div className="row">
                    <div className="col-12">
                         <p>
                            We've sent a code to +91 <b>{phoneNumber}</b>, didn't get it?
                            <Button onClick={resendOtp} disabled={disableResend} style={{ textTransform: 'none'}}>
                                {disableResend ? `Resend in ${timer} seconds` : 'Resend OTP'}
                            </Button>
                        </p>
                    </div>
                    {[1, 2, 3, 4].map((digit) => (
                        <div className="col-3" key={digit}>
                            <div className="mb-3 input-group-lg">
                                <input
                                maxLength="1"
                                name={`digit${digit}`}
                                type="text"
                                className="form-control text-center"
                                id={`digit-${digit}`}
                                placeholder="x"
                                value={otp[`digit${digit}`]}
                                onChange={(e) => handleOtpChange(e, `digit-${digit + 1}`)}
                                />
                            </div>
                        </div>
                    ))}
                </div>
                <div className="row">
                    <div className="col-12">
                        <p>
                            We also need below details for your registration -
                        </p>
                    </div>
                    <div className="col-9">
                        <div className="mb-3 input-group-lg">
                        <input
                            type="email"
                            id="email"
                            name="email"
                            className="form-control"
                            placeholder="Enter your email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                        </div>
                    </div>
                    <div className="col-9">
                        <div className="mb-3 input-group-lg">
                            <input
                                type="password"
                                name="password"
                                className="form-control"
                                placeholder="Enter your password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                        </div>
                    </div>
                    <div className="col-9">
                        <div className="mb-3 input-group-lg">
                        <input
                            type="text"
                            id="userName"
                            name="userName"
                            className="form-control"
                            placeholder="Enter your name"
                            value={userName}
                            onChange={(e) => setUserName(e.target.value)}
                            required
                        />
                        </div>
                    </div>
                    <div className="col-9">
                        <div className="mb-3 input-group-lg">
                            <input type="checkbox" htmlFor="demat" className="form-label m-1" onChange={(event) => setDemat(event.target.checked)} checked={isDemat}/>
                            <label htmlFor="demat" className="form-text">Need to open a Demat Account?</label><br></br>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12">
                        <p>
                            Didn’t get a code or facing any issue? Reach out to us on help@capitaltrends.in, we will sort it out for you.
                        </p>
                    </div>   
                </div>
            </DialogContent>
            <DialogActions>
                <Button autoFocus onClick={handleOtpSubmit}>
                    Submit
                </Button>
            </DialogActions>
        </BootstrapDialog>
    )
}


export default OtpVerify;